import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b';
import { createTeam, listTeams, type TeamSummary } from '../api';
import { getFreshBrowserSessionJwt, TeamSessionError } from '../auth/teamJwt';
import { PanelState, Wordmark } from '../components/Chrome';
import { consoleConfig } from '../config';
import {
  ALERT_ERROR,
  BUTTON_PRIMARY_LG,
  BUTTON_SECONDARY_LG,
  CARD,
  CALLBACK_SCREEN,
  EYEBROW,
  FIELD_LABEL,
  FIELD_WIDE,
} from '../controlStyles';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { describeCreateTeamFailure } from '../organization/CreateTeamPage';
import { NoTeamStep } from './NoTeamStep';
import { approveConnect, approveDevice, ConnectError, describeConnect, type ConnectDescription } from './connectApi';

/** Seam for tests: approval ends in a full navigation to the client's loopback URL. */
export const connectNavigation = {
  assign(url: string) {
    window.location.assign(url);
  },
};

/** The server accepts any case and spacing; this is only what the page shows. */
function normalizeUserCode(code: string): string {
  return code.trim().toUpperCase();
}

/** The CLI's device flow has no describe step; its client is always this one. */
const DEVICE_CLIENT: ConnectDescription = { clientName: 'the Nimbalyst CLI', redirectHost: '' };

type Mode = 'authorize' | 'device';

type Phase =
  | { status: 'loading' }
  | { status: 'enter-code' }
  | { status: 'consent'; client: ConnectDescription }
  | { status: 'approved'; client: ConnectDescription }
  | { status: 'denied'; client: ConnectDescription }
  | { status: 'failed'; title: string; message: string; signIn?: boolean };

/** Only teams the member has joined; the server counts nothing else. */
function activeTeams(teams: TeamSummary[]): TeamSummary[] {
  return teams.filter((team) => team.membershipType === 'active_member');
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The client as the sentence names it. Any app can register any name, so a
 * name from the server is only ever what the app calls itself.
 */
function clientLabel(client: ConnectDescription): string {
  if (client === DEVICE_CLIENT) return DEVICE_CLIENT.clientName!;
  const name = client.clientName?.trim();
  return name ? `an app calling itself ${name}` : 'an app';
}

/**
 * The approved code goes only to the loopback the page named. Anything else
 * (another host, another port, a non-web scheme) is refused before navigating.
 */
export function isExpectedRedirect(redirectTo: string, redirectHost: string): boolean {
  let url: URL;
  try {
    url = new URL(redirectTo);
  } catch {
    return false;
  }
  return (url.protocol === 'http:' || url.protocol === 'https:') && url.host === redirectHost;
}

/**
 * Every refusal the connect endpoints send, in words. The server's `error` is
 * a code (`expired_txn`, `invalid_session`, ...) and never reaches the page.
 */
function describeFailure(error: unknown, action: 'load' | 'approve', mode: Mode): { title: string; message: string } {
  const restart = mode === 'device'
    ? 'Run `nim login` again to get a new code.'
    : 'Restart the connection from your terminal.';
  if (error instanceof TeamSessionError && (error.kind === 'session-expired' || error.kind === 'malformed-token')) {
    return { title: 'Your Nimbalyst sign-in has ended', message: 'Sign in again, then approve again.' };
  }
  const code = error instanceof ConnectError ? error.code : null;
  const status = error instanceof ConnectError ? error.status : 0;
  if (status === 429) {
    return { title: 'Too many attempts', message: 'Wait a minute, then try again.' };
  }
  if (error instanceof RedirectMismatch) {
    return { title: 'The connection was not completed', message: `The server tried to send the approval somewhere other than the app shown on this page. ${restart}` };
  }
  if (code === 'expired_txn' || code === 'expired_code') {
    return mode === 'device'
      ? { title: 'This code has expired or was already used', message: restart }
      : { title: 'This connection request has expired', message: restart };
  }
  if (code === 'invalid_request') {
    return { title: 'This connection link is not valid', message: restart };
  }
  if (code === 'invalid_session' || status === 401) {
    return { title: 'Your sign-in has ended', message: 'Sign out, sign in again, and then approve again.' };
  }
  if (code === 'email_unverified') {
    return { title: 'Your email address is not verified', message: 'Sign in with a verified email address, then approve again.' };
  }
  if (code === 'member_inactive') {
    return { title: 'You are not an active member of a team', message: 'Accept your team invitation, or ask a team admin to restore your access, then approve again.' };
  }
  if (status >= 400 && status < 500) {
    return {
      title: action === 'load' ? 'This connection request could not be read' : 'The connection was not approved',
      message: restart,
    };
  }
  return {
    title: action === 'load' ? 'The connection request could not be loaded' : 'The connection was not approved',
    message: 'The Nimbalyst service could not be reached. Try again in a moment.',
  };
}

class RedirectMismatch extends Error {}

function ConnectFrame({ children }: { children: ReactNode }) {
  return (
    <main className={CALLBACK_SCREEN}>
      <div className="connect-frame grid w-[min(34rem,100%)] gap-6">
        <div className="justify-self-center"><Wordmark /></div>
        {children}
      </div>
    </main>
  );
}

function DeviceCodeForm({ initialCode, onSubmit }: { initialCode: string; onSubmit: (code: string) => void }) {
  const [code, setCode] = useState(initialCode);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = normalizeUserCode(code);
    if (normalized) onSubmit(normalized);
  };
  return (
    <form className={`connect-device-form ${CARD} grid gap-4 text-left`} onSubmit={submit} aria-labelledby="connect-device-heading">
      <div className="grid gap-2">
        <span className={EYEBROW}>Connect a device</span>
        <h1 id="connect-device-heading" className="m-0 text-[1.45rem] font-semibold leading-tight text-nim">Enter the code shown in your terminal</h1>
      </div>
      <label className="grid gap-1.5">
        <span className={FIELD_LABEL}>Code</span>
        <input
          className={`${FIELD_WIDE} font-mono text-[1.1rem] uppercase tracking-[0.18em]`}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="BCDF-GHJK"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          autoFocus
        />
      </label>
      <button className={`${BUTTON_PRIMARY_LG} justify-self-start`} type="submit" disabled={!code.trim()}>Continue</button>
    </form>
  );
}

function Consent({
  client,
  email,
  teams,
  userCode,
  busy,
  error,
  onAllow,
  onCreateTeamAndAllow,
  onDeny,
  denyLabel,
  confirmCode,
}: {
  client: ConnectDescription;
  email: string;
  teams: TeamSummary[];
  userCode: string | null;
  busy: boolean;
  error: string | null;
  onAllow: () => void;
  onCreateTeamAndAllow: (name: string) => void;
  onDeny: () => void;
  denyLabel: string;
  /** A code that arrived in the link, not typed by the user: it must be confirmed. */
  confirmCode: boolean;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const name = clientLabel(client);
  const heading = `Allow ${name}${client.redirectHost ? ` (${client.redirectHost})` : ''} to act as you in your Nimbalyst teams`;
  return (
    <section className={`connect-consent ${CARD} grid gap-5 text-left`} aria-label={heading}>
      <div className="grid gap-2">
        <span className={EYEBROW}>Connect</span>
        <h1 className="m-0 text-[1.45rem] font-semibold leading-tight text-nim">
          Allow {name}
          {client.redirectHost && <span className="connect-redirect-host font-normal text-nim-muted"> ({client.redirectHost})</span>}
          {' '}to act as you in your Nimbalyst teams
        </h1>
        <p className="m-0 text-[0.88rem] leading-relaxed text-nim-muted">
          It can read and write the wiki and trackers of every team below, with your role in each. A team you join later is included; removing you from a team removes its access too.
        </p>
      </div>
      <dl className="connect-identity m-0 grid gap-3">
        <div className="grid gap-1">
          <dt className={FIELD_LABEL}>Signed in as</dt>
          <dd className="m-0 text-[0.92rem] text-nim">{email}</dd>
        </div>
        {userCode && (
          <div className="grid gap-1">
            <dt className={FIELD_LABEL}>Device code</dt>
            <dd className="m-0 font-mono text-[0.95rem] tracking-[0.14em] text-nim">{userCode}</dd>
          </div>
        )}
        {userCode && confirmCode && (
          <div className="connect-code-confirmation grid gap-2">
            <p className="m-0 text-[0.86rem] leading-relaxed text-[var(--nim-warning)]">
              Only continue if you just ran `nim login` yourself and this is the code it shows. Someone who sends you this link could sign their terminal in as you.
            </p>
            <label className="flex cursor-pointer items-center gap-2 text-[0.88rem] text-nim">
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
              This matches the code in my terminal
            </label>
          </div>
        )}
        <div className="grid gap-1.5">
          <dt className={FIELD_LABEL}>Your teams</dt>
          <dd className="m-0">
            {teams.length === 0 ? (
              <NoTeamStep email={email} busy={busy} disabled={confirmCode && !confirmed} onCreate={onCreateTeamAndAllow} />
            ) : (
              <ul className="connect-team-list m-0 grid list-none gap-1 p-0">
                {teams.map((team) => (
                  <li className="flex items-center gap-2 rounded-md border border-nim bg-nim-secondary px-3 py-2 text-[0.86rem]" key={team.orgId}>
                    <DecorativeIcon icon="group" size={17} className="text-nim-muted" />
                    <span className="min-w-0 flex-1 truncate text-nim">{team.name}</span>
                    <span className="text-[0.74rem] capitalize text-nim-muted">{team.role}</span>
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </div>
      </dl>
      {error && <div className={ALERT_ERROR} role="alert">{error}</div>}
      <div className="connect-actions flex flex-wrap gap-2">
        {/* With no team the inline form is the primary action; this one leaves team creation to the app. */}
        <button
          className={teams.length === 0 ? BUTTON_SECONDARY_LG : BUTTON_PRIMARY_LG}
          type="button"
          disabled={busy || (confirmCode && !confirmed)}
          onClick={onAllow}
        >
          {busy ? 'Approving…' : teams.length === 0 ? 'Allow without a team' : 'Allow'}
        </button>
        <button className={BUTTON_SECONDARY_LG} type="button" disabled={busy} onClick={onDeny}>{denyLabel}</button>
      </div>
    </section>
  );
}

/**
 * `/connect?txn=…` (an agent's OAuth authorization) and `/connect/device` (the
 * CLI's device code). Mounted behind the Stytch sign-in, which returns here.
 */
export function ConnectPage({ mode }: { mode: Mode }) {
  const stytch = useStytchB2BClient();
  const { member } = useStytchMember();
  const [searchParams] = useSearchParams();
  const txn = searchParams.get('txn') ?? '';
  // verification_uri_complete carries the code. A link can come from anyone, so
  // a prefilled code needs an explicit "this is mine" before Allow is enabled.
  const prefilledCode = mode === 'device' ? normalizeUserCode(searchParams.get('user_code') ?? '') : '';
  const [userCode, setUserCode] = useState<string | null>(prefilledCode || null);
  const [codeTyped, setCodeTyped] = useState(false);
  const [phase, setPhase] = useState<Phase>(() => {
    if (mode === 'authorize') return { status: 'loading' };
    return prefilledCode ? { status: 'consent', client: DEVICE_CLIENT } : { status: 'enter-code' };
  });
  const [teams, setTeams] = useState<TeamSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getFreshBrowserSessionJwt(stytch.session, consoleConfig.sessionDurationMinutes)
      .then((jwt) => listTeams(jwt, controller.signal))
      .then((next) => { if (!controller.signal.aborted) setTeams(activeTeams(next)); })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('[connect] teams could not be loaded', error);
        const failure = describeFailure(error, 'load', mode);
        const expired = error instanceof TeamSessionError;
        setPhase({ status: 'failed', signIn: expired, ...(expired ? failure : { title: 'Your teams could not be loaded', message: failure.message }) });
      });
    return () => controller.abort();
  }, [stytch]);

  useEffect(() => {
    if (mode !== 'authorize') return;
    if (!txn) {
      setPhase({ status: 'failed', title: 'This connection link is incomplete', message: 'Start the connection again from the app that sent you here.' });
      return;
    }
    let cancelled = false;
    describeConnect(txn)
      // A teams-load failure that landed first stays on screen.
      .then((client) => { if (!cancelled) setPhase((current) => (current.status === 'failed' ? current : { status: 'consent', client })); })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('[connect] describe failed', error);
        setPhase({ status: 'failed', ...describeFailure(error, 'load', mode) });
      });
    return () => { cancelled = true; };
  }, [mode, txn]);

  /**
   * Approval does not need a team. The token the server mints names only the
   * verified email, and every team call re-checks membership, so a teamless
   * user's token reaches nothing until a team exists. `newTeamName` creates
   * that team first, through the same `POST /api/teams` as the New team page.
   */
  const allow = async (client: ConnectDescription, newTeamName?: string) => {
    if (!teams) return;
    setBusy(true);
    setApproveError(null);
    try {
      // Whatever org the session is in: the server accepts any of the user's
      // org JWTs, and exchanging here would switch the console's org under them.
      const sessionJwt = await getFreshBrowserSessionJwt(stytch.session, consoleConfig.sessionDurationMinutes);
      if (newTeamName) {
        let created: { orgId: string; name: string };
        try {
          created = await createTeam(sessionJwt, newTeamName);
        } catch (cause) {
          console.error('[connect] team creation failed', cause);
          setApproveError(describeCreateTeamFailure(cause));
          return;
        }
        setTeams([{ orgId: created.orgId, name: created.name, role: 'admin', membershipType: 'active_member', createdAt: new Date().toISOString(), projects: [] }]);
      }
      if (mode === 'authorize') {
        const { redirectTo } = await approveConnect(txn, sessionJwt);
        if (!isExpectedRedirect(redirectTo, client.redirectHost)) throw new RedirectMismatch(redirectTo);
        // A loopback URL owned by the client; only a full navigation reaches it.
        connectNavigation.assign(redirectTo);
        return;
      }
      await approveDevice(userCode!, sessionJwt);
      setPhase({ status: 'approved', client });
    } catch (error) {
      console.error('[connect] approval failed', error);
      const failure = describeFailure(error, 'approve', mode);
      setApproveError(`${failure.title}. ${failure.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (phase.status === 'enter-code') {
    return (
      <ConnectFrame>
        <DeviceCodeForm
          initialCode=""
          onSubmit={(code) => {
            setUserCode(code);
            setCodeTyped(true);
            setApproveError(null);
            setPhase({ status: 'consent', client: DEVICE_CLIENT });
          }}
        />
      </ConnectFrame>
    );
  }
  if (phase.status === 'failed') {
    return (
      <ConnectFrame>
        <PanelState tone="error" className="connect-failed" role="alert">
          <strong>{phase.title}</strong>
          <span>{phase.message}</span>
          {phase.signIn && (
            <button
              className={`${BUTTON_PRIMARY_LG} mt-3 justify-self-center`}
              type="button"
              // Signed out, this same URL shows the sign-in screen and comes back here.
              onClick={() => void stytch.session.revoke({ forceClear: true }).then(() => window.location.reload())}
            >
              Sign in again
            </button>
          )}
        </PanelState>
      </ConnectFrame>
    );
  }
  if (phase.status === 'denied') {
    return (
      <ConnectFrame>
        <PanelState tone="empty" className="connect-denied" role="status">
          <strong>{capitalize(clientLabel(phase.client))} was not connected</strong>
          <span>Nothing was shared, and the request expires on its own. You can close this tab.</span>
        </PanelState>
      </ConnectFrame>
    );
  }
  if (phase.status === 'approved') {
    return (
      <ConnectFrame>
        <PanelState tone="empty" className="connect-approved" role="status">
          <strong>Connected</strong>
          <span>Return to your terminal; it finishes signing in on its own. You can close this tab.</span>
        </PanelState>
      </ConnectFrame>
    );
  }
  if (phase.status === 'loading' || !teams) {
    return <ConnectFrame><PanelState>Loading the connection request…</PanelState></ConnectFrame>;
  }
  const { client } = phase;
  return (
    <ConnectFrame>
      <Consent
        client={client}
        email={member?.email_address ?? ''}
        teams={teams}
        userCode={mode === 'device' ? userCode : null}
        busy={busy}
        error={approveError}
        onAllow={() => void allow(client)}
        onCreateTeamAndAllow={(name) => void allow(client, name)}
        // No endpoint to call: the server lets an unapproved request expire.
        onDeny={() => setPhase({ status: 'denied', client })}
        denyLabel={mode === 'device' ? 'Cancel' : 'Deny'}
        confirmCode={mode === 'device' && !codeTyped}
      />
    </ConnectFrame>
  );
}
